/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';

import { isMobile } from '../../components/breakpoints';

import { QuickLink } from './components/ecomm-quick-link';

import { IEshopSiteMenuData } from './eshop-site-menu.data';
import { IEshopSiteMenuProps } from './eshop-site-menu.props.autogenerated';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

export interface IEshopSiteMenuViewProps extends IEshopSiteMenuProps<IEshopSiteMenuData> {
    qLinks: React.ReactNode[];
    quicklinksTitle: string;
    isProduction: boolean;
    homeUrl: string;
    cartUrl: string;
    cartTitle: string;
    signInTitle: string;
    languageTitles: string[];
    languageUrl: string[];
    search: React.ReactNode[];
}

/**
 *
 * EshopSiteMenu component
 * @extends {React.PureComponent<IEshopSiteMenuProps<IEshopSiteMenuData>>}
 */
class EshopSiteMenu extends React.PureComponent<IEshopSiteMenuProps<IEshopSiteMenuData>> {
    public state: any = { width: 0 };

    public componentDidMount(): void {
        if (!this.state.hasMounted) {
            window.addEventListener('resize', this._updateDimensions);

            this.setState({ width: window.innerWidth });
        }
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this._updateDimensions);
    }

    public render(): JSX.Element | null {
        const { config } = this.props;

        const LanguageTitles: string[] = [];
        const LanguageUrl: string[] = [];
        const searchSlot = this._getSlot('search');

        if (config.languages && config.languages.length > 0) {
            const languages = config.languages;
            const currentLanguageSubdomain = languages[0].languageUrl.destinationUrl;

            const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

            for (let i = 0; i < languages.length; ++i) {
                LanguageTitles.push(languages[`${i}`].languageName);
                LanguageUrl.push(currentUrl.replace(currentLanguageSubdomain, languages[`${i}`].languageUrl.destinationUrl));
            }
        }

        const EcommSiteMenuViewProps = {
            ...this.props,
            qLinks: this._generateQuickLinks(),
            quicklinksTitle: config.quickLinksTitle,
            isProduction: config.isProduction,
            homeUrl: config.homeUrl.destinationUrl,
            cartUrl: config.cartUrl.destinationUrl,
            cartTitle: config.cartTitle,
            signInTitle: config.signInTitle,
            languageTitles: LanguageTitles,
            languageUrl: LanguageUrl,
            search: ArrayExtensions.hasElements(searchSlot) ? searchSlot : []
        };

        return this.props.renderView(EcommSiteMenuViewProps);
    }

    private _generateQuickLinks(): JSX.Element[] | null {
        const {
            config,
            context,
            data: { hierarchy }
        } = this.props;

        const qLinks: JSX.Element[] = [];
        const locale = context.actionContext.requestContext.locale;
        const localeCode = context.actionContext.requestContext.locale.split('-')[0];

        if (hierarchy && hierarchy.result) {
            const hierarchies = hierarchy.result;

            if (config.quicklinks) {
                for (let i = 0; i < config.quicklinks.length; i++) {
                    const quicklinkName = config.quicklinks[`${i}`].quicklinkName;
                    const categoryIndex = hierarchies.findIndex(item => item.Name?.toLowerCase() === quicklinkName.toLowerCase());

                    if (categoryIndex > -1) {
                        const filter = config.quicklinks[`${i}`].filterName;
                        const categoryId = hierarchies[`${categoryIndex}`].RecordId;
                        const categoryLocale = hierarchies[`${categoryIndex}`].NameTranslations?.find(
                            (item: TextValueTranslation) => item.Language === locale
                        );
                        const categoryLocaleName = categoryLocale ? (categoryLocale.Text ? categoryLocale.Text : '') : quicklinkName;
                        const categoryUrl = config.quicklinks[`${i}`].categoryLink?.destinationUrl;
                        const url = categoryUrl ? categoryUrl : '';
                        const target = config.quicklinks[`${i}`].isPortal ? '_blank' : '_self';

                        qLinks.push(
                            <QuickLink
                                context={context}
                                hierarchy={hierarchies[`${categoryIndex}`]}
                                name={categoryLocaleName}
                                categoryId={categoryId}
                                url={url}
                                target={target}
                                filterName={filter}
                                isPortal={config.quicklinks[`${i}`].isPortal}
                                mobileMode={isMobile(this.state.width)}
                                locale={localeCode}
                            />
                        );
                    }
                }
            }
        }

        return qLinks;
    }

    private _updateDimensions = () => {
        this.setState({ width: window.innerWidth });
    };

    /**
     * Get the slot.
     * @param slotName - Name of the slot.
     * @returns The slot.
     */
    private _getSlot(slotName: string): React.ReactNode[] | null {
        const { slots } = this.props;
        // eslint-disable-next-line security/detect-object-injection
        return (slots && slots[slotName] && slots[slotName].length && slots[slotName]) || null;
    }
}

export default EshopSiteMenu;
